import CloseIcon from '@mui/icons-material/Close';
import { Checkbox,Divider,FormControl,FormControlLabel,FormGroup,FormLabel,Radio,RadioGroup,Stack,Typography } from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import { useContext,useEffect,useRef,useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UserContext } from '../../../context/UserContext';
import { trackEventMatomo } from '../../../helpers/matomo';
import { avaliacaoType,ModalOptionsProps,Ticket } from '../../../types/reports/types';

export default function ModalOptions({
  showRange,
  setShowRange,
  chamados,
  temChamados,
  showNumbers,
  setShowNumbers,
  exibeTipo,
  setExibeTipo,
  ocultaNomeEmpresa,
  setOcultaNomeEmpresa,
  open,
  handleOpen,
  showNotSelected,
  setShowNotSelected,
  showScoring,
  setShowScoring,
  showHideFields,
  setShowHideFields,
  showHeaders,
  setShowHeaders,
  hideHierarchy,
  setHideHierarchy,
  hideEquipe,
  setHideEquipe,
  hideUser,
  setHideUser,
  hideIdent,
  setHideIdent,
  hideAprov,
  setHideAprov,
  hideDate,
  setHideDate,
  hideNAplica,
  setHideNAplica,
  radioOption,
  setRadioOption,
  form,
  showDuplicatas,
  setShowDuplicatas,
  hideFiles,
  setHideFiles,
  exibeDetalheHospedeiro,
  setExibeDetalheHospedeiro,
}: ModalOptionsProps) {
  const { t } = useTranslation('translation');
  const { hierarchyLabel } = useContext(UserContext);
  const [disable, setDisable] = useState(false);
  const checkboxRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const saved = localStorage.getItem("valoresOpcoes");
    if (saved) {
      const parsed = JSON.parse(saved);
      setShowRange(parsed?.range ?? true);
      setShowHeaders(parsed?.cabecalho);
      setOcultaNomeEmpresa(parsed?.nomeEmpresa);
      setHideHierarchy({
        cliente: parsed?.cliente,
        local: parsed?.local,
        conjunto: parsed?.conjunto,
        unidade: parsed?.unidade,
      });
      setExibeTipo(parsed?.tipoSubitem);
      setShowNumbers(parsed?.numeroItem);
      setHideAprov(parsed?.listaAprovados);
      setShowScoring(parsed?.scoring);
      setHideIdent(parsed?.identificador);
      setShowNotSelected(parsed?.nSelecionado);
      setHideNAplica(parsed?.nAplica);
      setRadioOption(parsed?.chamados);
      setHideDate({ inicio: parsed?.inicio, fim: parsed?.fim });
      setHideUser(parsed?.usuario);
      setHideEquipe(parsed?.equipe);
      setHideFiles(parsed?.arquivos);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    localStorage.setItem(
      "valoresOpcoes",
      JSON.stringify({
        range: showRange,
        cabecalho: showHeaders,
        nomeEmpresa: ocultaNomeEmpresa,
        cliente: hideHierarchy.cliente,
        local: hideHierarchy.local,
        conjunto: hideHierarchy.conjunto,
        unidade: hideHierarchy.unidade,
        tipoSubitem: exibeTipo,
        numeroItem: showNumbers,
        listaAprovados: hideAprov,
        scoring: showScoring,
        identificador: hideIdent,
        nSelecionado: showNotSelected,
        nAplica: hideNAplica,
        chamados: radioOption,
        inicio: hideDate.inicio,
        fim: hideDate.fim,
        usuario: hideUser,
        equipe: hideEquipe,
        arquivos: hideFiles,
      }),
    );
  }, [
    showRange,
    showHeaders,
    ocultaNomeEmpresa,
    hideHierarchy,
    exibeTipo,
    showNumbers,
    hideAprov,
    showScoring,
    hideIdent,
    showNotSelected,
    hideNAplica,
    radioOption,
    hideDate,
    hideUser,
    hideEquipe,
    hideFiles
  ]);

  useEffect(() => {
    if (chamados) {
      const hasFormDetails = chamados.some((item) => item.ticketDetails?.formulario?.some((elem: Ticket) => elem.historicoChamado));

      const hasSubDetails =
        chamados.some((item) => Object.values(item.ticketDetails?.subitens ?? {})?.some((elem) => elem.chamados?.some((sub) => sub.historicoChamado)));
      if (!hasFormDetails && !hasSubDetails) setDisable(true);
    }
  }, [chamados]);

  const hasNAplica = () => form?.itens?.some(item =>
    item?.subitens?.some(sub =>
      sub?.avaliacao?.some((aval: avaliacaoType) => aval?.valor === -1)
    )
  );

  const handleHierarchyChange = () => {
    const allFalse = Object.values(hideHierarchy).every((v) => v === false);
    const newState = Object.keys(hideHierarchy).reduce(
      (acc, key) => ({
        ...acc,
        [key]: allFalse,
      }),
      {} as typeof hideHierarchy,
    );
    setHideHierarchy(newState);
  };

  const toggle = (setter: Function, value: boolean) => {
    trackEventMatomo('Relatórios', 'click', 'checkbox', 'seleciona valor');
    setter(!value);
  };

  const hasFiles = form?.itens?.some(i => i.subitens.some(s => s.arquivos && s.arquivos.length > 0));

  return (
    <Dialog fullWidth maxWidth="md" open={open} onClose={() => handleOpen()}>
      <DialogTitle sx={{ m: 0, p: 2 }}>Exibir/Ocultar informações do relatório</DialogTitle>
      <IconButton
        aria-label="close"
        onClick={() => handleOpen()}
        sx={{ position: "absolute", right: 8, top: 8, color: (theme) => theme.palette.grey[500] }}>
        <CloseIcon />
      </IconButton>

      <DialogContent dividers>
        {/* Cabeçalho e empresa */}
        {!form.preenchimentoDeslogado && (
          <FormControlLabel
            control={<Checkbox size="small" checked={!showHeaders} onClick={() => toggle(setShowHeaders, showHeaders)} />}
            label={t("checklists.printConfig.Ocultar_cabecalho")}
          />
        )}
        <FormControlLabel
          control={<Checkbox size="small" checked={ocultaNomeEmpresa} onClick={() => toggle(setOcultaNomeEmpresa, ocultaNomeEmpresa)} />}
          label="Ocultar nome da empresa"
        />

        {!form.preenchimentoDeslogado && (
          <>
            <Divider />
            <Stack direction="row" spacing={2}>
              {/* Hierarquia */}
              <Stack width="100%" direction="column">
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      inputRef={checkboxRef}
                      checked={Object.values(hideHierarchy).every((v) => v === false)}
                      indeterminate={Object.values(hideHierarchy).some((v) => v === false)}
                      onChange={handleHierarchyChange}
                    />
                  }
                  label="Hierarquia"
                />
                <Divider />
                <FormGroup>
                  {["cliente", "local", "conjunto", "unidade"].map((level) => (
                    <FormControlLabel
                      key={level}
                      control={
                        <Checkbox
                          size="small"
                          checked={!hideHierarchy[level as keyof typeof hideHierarchy]}
                          onClick={() => setHideHierarchy({ ...hideHierarchy, [level]: !hideHierarchy[level as keyof typeof hideHierarchy] })}
                        />
                      }
                      label={`Ocultar ${hierarchyLabel[level as keyof typeof hierarchyLabel]}`}
                    />
                  ))}
                </FormGroup>
              </Stack>

              {/* Formulário */}
              <Stack direction="column" width="100%">
                <Typography variant="subtitle1" py={0.6}>
                  {t("checklists.printConfig.Formulario")}
                </Typography>
                <Divider />
                <FormGroup>
                  <FormControlLabel
                    control={<Checkbox size="small" checked={exibeTipo} onClick={() => toggle(setExibeTipo, exibeTipo)} />}
                    label={t("checklists.printConfig.Exibir_tipo_subitem")}
                  />
                  <FormControlLabel
                    control={<Checkbox size="small" checked={showNumbers} onClick={() => toggle(setShowNumbers, showNumbers)} />}
                    label={t("checklists.printConfig.Exibir_numeracao")}
                  />
                  {form.identificador !== "-" && (
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          disabled={!form?.identificador}
                          checked={!hideIdent || !form?.identificador}
                          onClick={() => toggle(setHideIdent, hideIdent)}
                        />
                      }
                      label={t("checklists.printConfig.Ocultar_identificador")}
                    />
                  )}
                  {form.nomeAprovador && form?.nomeAprovador?.length > 0 && (
                    <FormControlLabel
                      control={<Checkbox size="small" checked={!hideAprov} onClick={() => toggle(setHideAprov, hideAprov)} />}
                      label={t("checklists.printConfig.Exibir_lista_aprovados")}
                    />
                  )}
                  {form.score && (
                    <FormControlLabel
                      control={<Checkbox size="small" checked={showScoring} onClick={() => toggle(setShowScoring, showScoring)} />}
                      label={t("checklists.printConfig.Ocultar_informacoes")}
                    />
                  )}
                  {form.range && (
                    <FormControlLabel
                      control={<Checkbox size="small" checked={showRange} onClick={() => toggle(setShowRange, showRange)} />}
                      label={t("checklists.printConfig.Ocultar_range")}
                    />
                  )}
                  {form.camposOcultos && (
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          disabled={!form?.camposOcultos}
                          checked={showHideFields || !form?.camposOcultos}
                          onClick={() => toggle(setShowHideFields, showHideFields)}
                        />
                      }
                      label={t("checklists.printConfig.Exibir_campos_ocultos")}
                    />
                  )}
                  {hasFiles && (
                    <FormControlLabel
                      control={<Checkbox size="small" disabled={!hasFiles} checked={hideFiles || !hasFiles} onClick={() => toggle(setHideFiles, hideFiles)} />}
                      label={t("checklists.printConfig.Ocultar_arquivos")}
                    />
                  )}
                  <FormControlLabel
                    control={<Checkbox size="small" checked={showNotSelected} onClick={() => toggle(setShowNotSelected, showNotSelected)} />}
                    label={t("checklists.printConfig.Exibir_nao_selecionadas")}
                  />
                  {!hasNAplica() && (
                    <FormControlLabel
                      control={<Checkbox size="small" checked={!hideNAplica || hasNAplica()} onClick={() => toggle(setHideNAplica, hideNAplica)} />}
                      label={t("checklists.printConfig.Ocultar_nao_aplica")}
                    />
                  )}
                  {form?.detalheHospedeiro && (
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          checked={!exibeDetalheHospedeiro}
                          onClick={() => toggle(setExibeDetalheHospedeiro, exibeDetalheHospedeiro)}
                        />
                      }
                      label={t("Reports.ocultaHospedeiro")}
                    />
                  )}
                </FormGroup>
              </Stack>
            </Stack>
            <Divider />
          </>
        )}

        {/* Chamados, Datas e Associação */}
        <Stack spacing={2} direction="row" justifyContent="space-between" alignItems="flex-start">
          {temChamados && (
            <FormControl sx={{ width: "100%" }}>
              <FormLabel sx={{ py: 1 }}>Exibição de chamados</FormLabel>
              <Divider />
              <RadioGroup defaultValue={radioOption}>
                {[
                  { value: "hide", label: "Ocultar informações" },
                  { value: "some", label: "Informações essenciais" },
                  { value: "all", label: "Informações completas", disabled: disable },
                ]?.map((opt) => (
                  <FormControlLabel
                    key={opt.value}
                    value={opt.value}
                    control={<Radio disabled={opt.disabled} onChange={(e) => setRadioOption(e.target.value)} />}
                    label={opt.label}
                  />
                ))}
              </RadioGroup>
              <FormControlLabel
                disabled={radioOption === "hide"}
                control={<Checkbox checked={showDuplicatas} onChange={() => setShowDuplicatas(!showDuplicatas)} />}
                label="Exibir duplicados"
              />
            </FormControl>
          )}

          <Stack direction="column" width="70%">
            <Typography variant="subtitle1" py={0.7}>
              {t("Reports.ticketList.dates")}
            </Typography>
            <Divider />
            <FormGroup>
              <FormControlLabel
                control={<Checkbox size="small" checked={!hideDate.inicio} onClick={() => setHideDate({ ...hideDate, inicio: !hideDate.inicio })} />}
                label={t("Reports.ticketList.hideStart")}
              />
              <FormControlLabel
                control={<Checkbox size="small" checked={!hideDate.fim} onClick={() => setHideDate({ ...hideDate, fim: !hideDate.fim })} />}
                label={t("Reports.ticketList.hideEnd")}
              />
            </FormGroup>
          </Stack>

          {!form.preenchimentoDeslogado && (
            <Stack direction="column" width="80%">
              <Typography variant="subtitle1" py={0.7}>
                Associação
              </Typography>
              <Divider />
              <FormGroup>
                <FormControlLabel
                  control={<Checkbox size="small" checked={!hideUser} onClick={() => toggle(setHideUser, hideUser)} />}
                  label="Ocultar usuário"
                />
                <FormControlLabel
                  control={<Checkbox size="small" checked={!hideEquipe} onClick={() => toggle(setHideEquipe, hideEquipe)} />}
                  label={`Exibir ${t("team")}`}
                />
              </FormGroup>
            </Stack>
          )}
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
